/**
 * CENTRALIZED NAVIGATION CONFIGURATION
 * ===================================
 * 
 * This file is the SINGLE SOURCE OF TRUTH for all navigation, routing, and tool configuration.
 * 
 * When adding new tools/routes:
 * 1. Add the route to navigationConfig below
 * 2. Update the AppTool type if needed
 * 3. Everything else (sidebar, analytics, status badges, etc.) will automatically work
 * 
 * Benefits:
 * - Single place to manage all route configuration
 * - Automatic consistency across navigation, analytics, and status
 * - Easy to hide/show tools by setting visible: false
 * - Configurable external links, status badges, etc.
 */

export type FeatureStatus = 'alpha' | 'beta' | 'coming-soon' | 'community'

// App tools type - should be kept in sync with the main app
export type AppTool =
  | 'dashboard'
  | 'survey-analysis'
  | 'file-manager'
  | 'transcription'
  | 'settings'
  | 'note-taking'
  | 'atr'
  | 'chat-translator'
  | 'community-apps'
  | 'tab-plan-generator'
  | 'visual-semiotics'
  | 'r3-chat'
  | 'ai-listening'
  | 'agent-builder'

export interface NavigationConfig {
  [path: string]: {
    toolId: AppTool
    displayName: string
    pageName: string // For analytics
    toolName?: string // For analytics (null for non-tool pages)
    status?: FeatureStatus
    visible?: boolean // Whether to show in navigation (default: true)
    external?: boolean // Whether this opens in external tab (default: false)
    // Can be extended with more metadata in the future
    // permissions?: string[]
    // description?: string
  }
}

/**
 * Navigation configuration - single source of truth for all app navigation
 * Update this to change routes, tool info, status, visibility across the entire application
 */
export const navigationConfig: NavigationConfig = {
  '/dashboard': {
    toolId: 'dashboard',
    displayName: 'Dashboard',
    pageName: 'dashboard',
    toolName: undefined // Dashboard is not a tool page
    // No status for stable features
  },
  '/chat': {
    toolId: 'r3-chat',
    displayName: 'Ask Ros',
    pageName: 'r3-chat',
    toolName: 'r3-chat',
    status: 'alpha'
  },
  '/survey-analysis': {
    toolId: 'survey-analysis',
    displayName: 'Survey Guard',
    pageName: 'survey-guard',
    toolName: 'survey-guard',
    status: 'beta'
  },
  '/files': {
    toolId: 'file-manager',
    displayName: 'My Files',
    pageName: 'file-manager',
    toolName: 'file-manager'
  },
  '/note-taking': {
    toolId: 'note-taking',
    displayName: 'Interview Notetaker',
    pageName: 'interview-notetaker',
    toolName: 'interview-notetaker',
    status: 'beta'
  },
  '/transcription': {
    toolId: 'transcription',
    displayName: 'Transcription Assistant',
    pageName: 'transcription-assistant',
    toolName: 'transcription-assistant',
    status: 'beta'
  },
  '/tab-plan-generator': {
    toolId: 'tab-plan-generator',
    displayName: 'Tab Plan Generator',
    pageName: 'tab-plan-generator',
    toolName: 'tab-plan-generator',
    status: 'alpha'
  },
  '/community-apps': {
    toolId: 'community-apps',
    displayName: 'Community Apps',
    pageName: 'community-apps',
    toolName: undefined // Community apps is not a tool page
  },
  '/visual-semiotics': {
    toolId: 'visual-semiotics',
    displayName: 'Visual Semiotics',
    pageName: 'visual-semiotics',
    toolName: 'visual-semiotics',
    status: 'alpha'
  },
  '/atr': {
    toolId: 'atr',
    displayName: 'ATR Generator',
    pageName: 'atr-generator',
    toolName: 'atr-generator',
    status: 'community', // Changed from 'beta' to 'community'
    external: true // Opens in external tab
  },
  '/chat-translator': {
    toolId: 'chat-translator',
    displayName: 'Polyglot Studio',
    pageName: 'polyglot-studio',
    toolName: 'polyglot-studio',
    status: 'coming-soon',
    visible: false // Hide from navigation - now controlled by centralized config
  },
  '/settings': {
    toolId: 'settings',
    displayName: 'Settings',
    pageName: 'settings',
    toolName: undefined, // Settings is not a tool page
    visible: false // Hide from sidebar - now in user profile dropdown
    // No status for stable features
  },
  '/ai-listening': {
    toolId: 'ai-listening',
    displayName: 'AI Listening',
    pageName: 'ai-listening',
    toolName: 'ai-listening',
    status: 'alpha'
  },
  '/agent-builder': {
    toolId: 'agent-builder',
    displayName: 'Agent Builder',
    pageName: 'agent-builder',
    toolName: 'agent-builder',
    status: 'alpha',
    visible: false
  }
}

// Legacy support - can be removed once everything migrates to navigationConfig
export const routeStatusConfig: Record<string, { status?: FeatureStatus }> = Object.fromEntries(
  Object.entries(navigationConfig).map(([path, config]) => [
    path,
    { status: config.status }
  ])
)

/**
 * Get the feature status for a given route path
 * @param pathname - The route pathname (e.g., '/note-taking')
 * @returns The feature status or null if no status is defined
 */
export const getStatusForRoute = (pathname: string): FeatureStatus | null => {
  const config = navigationConfig[pathname]
  return config?.status || null
}

/**
 * Get the tool ID for a given route path
 * @param pathname - The route pathname (e.g., '/note-taking')
 * @returns The tool ID or null if not found
 */
export const getToolForRoute = (pathname: string): AppTool | null => {
  const config = navigationConfig[pathname]
  return config?.toolId || null
}

/**
 * Get the display name for a given route path
 * @param pathname - The route pathname (e.g., '/note-taking') 
 * @returns The display name or null if not found
 */
export const getDisplayNameForRoute = (pathname: string): string | null => {
  const config = navigationConfig[pathname]
  return config?.displayName || null
}

/**
 * Get all navigation items (filtered by visibility)
 * @param includeHidden - Whether to include hidden navigation items (default: false)
 * @returns Array of navigation items with their paths and config
 */
export const getNavigationItems = (includeHidden = false): Array<{path: string} & NavigationConfig[string]> => {
  return Object.entries(navigationConfig)
    .filter(([, config]) => includeHidden || config.visible !== false)
    .map(([path, config]) => ({ path, ...config }))
}

/**
 * Get all routes with a specific status
 * @param status - The status to filter by
 * @returns Array of route paths with the specified status
 */
export const getRoutesByStatus = (status: FeatureStatus): string[] => {
  return Object.entries(navigationConfig)
    .filter(([, config]) => config.status === status)
    .map(([path]) => path)
}

/**
 * Check if a route has a specific status
 * @param pathname - The route pathname
 * @param status - The status to check for
 * @returns True if the route has the specified status
 */
export const routeHasStatus = (pathname: string, status: FeatureStatus): boolean => {
  return getStatusForRoute(pathname) === status
}

/**
 * Create path-to-page mapping for analytics (derived from navigation config)
 * This ensures consistency between navigation and analytics
 */
export const createPathToPageMap = (): Record<string, string> => {
  const pathToPageMap: Record<string, string> = {}
  Object.entries(navigationConfig).forEach(([path, config]) => {
    pathToPageMap[path] = config.pageName
  })
  return pathToPageMap
}

/**
 * Create page-to-tool mapping for analytics (derived from navigation config)
 * This ensures consistency between navigation and analytics
 */
export const createPageToToolMap = (): Record<string, string | null> => {
  const pageToToolMap: Record<string, string | null> = {}
  Object.entries(navigationConfig).forEach(([, config]) => {
    pageToToolMap[config.pageName] = config.toolName || null
  })
  return pageToToolMap
}
